import { useEffect, useState } from "react";
import { usePageState } from "@/contexts/NavigationContext";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  PlusIcon, BankIcon, VaultIcon, ArrowsLeftRightIcon, CheckCircleIcon, XCircleIcon, WalletIcon
} from "@phosphor-icons/react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

export default function Treasury() {
  const [tab, setTab] = useState("dashboard");
  const [summary, setSummary] = useState(null);
  const [movements, setMovements] = useState([]);
  const [filter, setFilter] = usePageState("filter", { cuenta_id: "", conciliado: "" });
  const [openMov, setOpenMov] = useState(false);
  const [openAcc, setOpenAcc] = useState(false);
  const [openTransfer, setOpenTransfer] = useState(false);
  const [editAcc, setEditAcc] = useState(null);

  const loadAll = async () => {
    const [s, m] = await Promise.all([
      api.get("/treasury/summary"),
      api.get("/treasury/movements", { params: {
        ...(filter.cuenta_id ? { cuenta_id: filter.cuenta_id } : {}),
        ...(filter.conciliado !== "" ? { conciliado: filter.conciliado } : {}),
      }}),
    ]);
    setSummary(s.data); setMovements(m.data);
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [filter]);

  const reconcile = async (mv) => {
    try { await api.post(`/treasury/movements/${mv.id}/reconcile`); toast.success("Estado conciliación actualizado"); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!summary) return <Layout title="Tesorería"><div className="text-text-muted">Cargando…</div></Layout>;

  return (
    <Layout
      title="Tesorería"
      subtitle={`${summary.cuentas.length} cuentas · ${summary.movimientos_pendientes_conciliar} mov. pendientes de conciliar`}
      actions={
        <div className="flex gap-2">
          <button onClick={() => setOpenTransfer(true)} data-testid="transfer-btn" className="px-3 py-2 text-sm border border-border rounded-md text-text-secondary hover:text-agro-yellow hover:border-agro-yellow/40 flex items-center gap-2">
            <ArrowsLeftRightIcon size={14} /> Transferir
          </button>
          <button onClick={() => { setEditAcc(null); setOpenAcc(true); }} data-testid="new-account-btn" className="px-3 py-2 text-sm border border-border rounded-md text-text-secondary hover:text-text-primary hover:border-agro-green/40 flex items-center gap-2">
            <BankIcon size={14} /> Nueva cuenta
          </button>
          <button onClick={() => setOpenMov(true)} data-testid="new-movement-btn" className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 transition-all active:scale-95">
            <PlusIcon size={16} weight="bold" /> Movimiento
          </button>
        </div>
      }
    >
      {/* Top KPI */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="md:col-span-1 bg-gradient-to-br from-agro-green/15 via-card to-card border border-agro-green/30 rounded-lg p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-agro-green font-medium">
            <WalletIcon size={14} weight="duotone" /> Efectivo disponible
          </div>
          <div className="font-heading text-3xl font-semibold text-text-primary mt-3" data-testid="treasury-total">{fmtL(summary.total_efectivo)}</div>
          <div className="text-xs text-text-muted mt-2">{summary.cuentas.length} cuentas activas</div>
        </div>
        {summary.cuentas.slice(0, 3).map((c) => (
          <div key={c.id} className="bg-card border border-border/60 rounded-lg p-5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-text-muted">{c.codigo}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded border ${c.tipo === "caja" ? "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30" : "bg-blue-500/10 text-blue-400 border-blue-500/30"}`}>{c.tipo}</span>
            </div>
            <div className="text-sm text-text-primary mt-2 truncate">{c.nombre}</div>
            <div className="font-heading text-xl font-semibold text-agro-green mt-2">{c.moneda === "USD" ? "$" : "L."} {Number(c.saldo_actual).toLocaleString("es-HN", { minimumFractionDigits: 2 })}</div>
            <div className="text-[10px] text-text-muted mt-1">{c.banco || "Caja interna"}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-4 flex gap-1">
        {[
          { k: "dashboard", l: "Flujo de Caja" },
          { k: "cuentas", l: "Cuentas" },
          { k: "movimientos", l: "Movimientos" },
          { k: "conciliacion", l: "Conciliación" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-4 py-2.5 text-sm transition-all relative ${tab === t.k ? "text-agro-green" : "text-text-secondary hover:text-text-primary"}`}>
            {t.l}
            {tab === t.k && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-agro-green" />}
          </button>
        ))}
      </div>

      {tab === "dashboard" && (
        <div className="bg-card border border-border/60 rounded-lg p-5" data-testid="treasury-cashflow">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-text-muted">Tendencia</div>
              <h3 className="font-heading text-lg font-medium mt-1">Flujo de caja (30 días)</h3>
            </div>
            <div className="text-right">
              <div className="text-xs text-text-muted">Neto del período</div>
              <div className="font-heading text-xl text-agro-green font-semibold">{fmtL(summary.flujo_30_dias.reduce((s, d) => s + d.neto, 0))}</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={summary.flujo_30_dias} margin={{ left: -10, right: 8, top: 8 }}>
              <defs>
                <linearGradient id="ing" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#02BF44" stopOpacity={0.5} /><stop offset="100%" stopColor="#02BF44" stopOpacity={0} /></linearGradient>
                <linearGradient id="egr" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#EF4444" stopOpacity={0.4} /><stop offset="100%" stopColor="#EF4444" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#222D26" vertical={false} />
              <XAxis dataKey="fecha" stroke="#68756D" fontSize={10} tickFormatter={(d) => new Date(d).toLocaleDateString("es-HN", { day: "2-digit", month: "short" })} />
              <YAxis stroke="#68756D" fontSize={10} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
              <Tooltip contentStyle={{ background: "#111612", border: "1px solid #222D26", borderRadius: 8, fontSize: 12 }} formatter={(v) => fmtL(v)} />
              <Area type="monotone" dataKey="ingresos" stroke="#02BF44" strokeWidth={2} fill="url(#ing)" />
              <Area type="monotone" dataKey="egresos" stroke="#EF4444" strokeWidth={2} fill="url(#egr)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {tab === "cuentas" && (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">Código</th><th className="p-3">Cuenta</th><th className="p-3">Banco</th>
                <th className="p-3">N° Cuenta</th><th className="p-3">Moneda</th>
                <th className="p-3 text-right">Saldo inicial</th><th className="p-3 text-right">Saldo actual</th><th className="p-3 pr-5"></th>
              </tr>
            </thead>
            <tbody>
              {summary.cuentas.map((c) => (
                <tr key={c.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`account-row-${c.codigo}`}>
                  <td className="p-3 pl-5 font-mono text-xs text-agro-green">{c.codigo}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {c.tipo === "caja" ? <VaultIcon size={16} className="text-agro-yellow" weight="duotone" /> : <BankIcon size={16} className="text-blue-400" weight="duotone" />}
                      <span className="text-sm">{c.nombre}</span>
                    </div>
                  </td>
                  <td className="p-3 text-sm text-text-secondary">{c.banco || "—"}</td>
                  <td className="p-3 text-xs font-mono text-text-muted">{c.numero_cuenta || "—"}</td>
                  <td className="p-3 text-xs">{c.moneda}</td>
                  <td className="p-3 text-right text-sm text-text-secondary">{fmtL(c.saldo_inicial)}</td>
                  <td className="p-3 text-right text-sm font-medium text-agro-green">{fmtL(c.saldo_actual)}</td>
                  <td className="p-3 pr-5 text-right">
                    <button onClick={() => { setEditAcc(c); setOpenAcc(true); }} className="text-xs text-text-muted hover:text-agro-green">Editar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(tab === "movimientos" || tab === "conciliacion") && (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border/60 flex gap-3 flex-wrap">
            <select value={filter.cuenta_id} onChange={(e) => setFilter({...filter, cuenta_id: e.target.value})} data-testid="filter-cuenta" className="bg-app border border-border rounded-md px-3 py-2 text-sm">
              <option value="">Todas las cuentas</option>
              {summary.cuentas.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)}
            </select>
            {tab === "conciliacion" && (
              <select value={filter.conciliado} onChange={(e) => setFilter({...filter, conciliado: e.target.value === "" ? "" : e.target.value === "true"})} className="bg-app border border-border rounded-md px-3 py-2 text-sm">
                <option value="">Todos</option>
                <option value="false">Pendientes</option>
                <option value="true">Conciliados</option>
              </select>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-sidebar">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-3 pl-5">Fecha</th><th className="p-3">Cuenta</th><th className="p-3">Concepto</th>
                  <th className="p-3">Tipo</th><th className="p-3">Referencia</th>
                  <th className="p-3 text-right">Monto</th>{tab === "conciliacion" && <th className="p-3 text-center">Estado</th>}
                </tr>
              </thead>
              <tbody>
                {movements.length === 0 ? <tr><td colSpan="7" className="p-12 text-center text-text-muted">Sin movimientos</td></tr>
                : movements.map((m) => {
                  const isOut = m.tipo === "egreso" || m.tipo === "transferencia_out";
                  return (
                    <tr key={m.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`movement-row-${m.id}`}>
                      <td className="p-3 pl-5 text-xs font-mono text-text-secondary">{fmtDate(m.fecha)}</td>
                      <td className="p-3 text-sm">{m.cuenta_nombre}</td>
                      <td className="p-3 text-sm">{m.concepto}</td>
                      <td className="p-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded ${isOut ? "bg-red-500/10 text-red-400" : "bg-agro-green/10 text-agro-green"}`}>{m.tipo.replace("_", " ")}</span></td>
                      <td className="p-3 font-mono text-xs text-text-muted">{m.referencia || "—"}</td>
                      <td className={`p-3 text-right text-sm font-medium ${isOut ? "text-red-400" : "text-agro-green"}`}>{isOut ? "-" : "+"}{fmtL(m.monto)}</td>
                      {tab === "conciliacion" && (
                        <td className="p-3 text-center">
                          <button onClick={() => reconcile(m)} data-testid={`reconcile-${m.id}`} className={`text-xs px-3 py-1 rounded border ${m.conciliado ? "bg-agro-green/10 text-agro-green border-agro-green/30" : "bg-app text-text-muted border-border hover:border-agro-green/40"}`}>
                            {m.conciliado ? <span className="flex items-center gap-1"><CheckCircleIcon size={12} weight="fill" /> Conciliado</span> : <span className="flex items-center gap-1"><XCircleIcon size={12} /> Pendiente</span>}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openMov && <MovementModal accounts={summary.cuentas} onClose={() => setOpenMov(false)} onSaved={() => { setOpenMov(false); loadAll(); }} />}
      {openAcc && <AccountModal account={editAcc} onClose={() => setOpenAcc(false)} onSaved={() => { setOpenAcc(false); loadAll(); }} />}
      {openTransfer && <TransferModal accounts={summary.cuentas} onClose={() => setOpenTransfer(false)} onSaved={() => { setOpenTransfer(false); loadAll(); }} />}
    </Layout>
  );
}

function MovementModal({ accounts, onClose, onSaved }) {
  const [f, setF] = useState({ cuenta_id: accounts[0]?.id || "", tipo: "ingreso", concepto: "", monto: 0, referencia: "", metodo: "efectivo" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/treasury/movements", { ...f, monto: Number(f.monto) }); toast.success("Movimiento registrado"); onSaved(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const m = useModalForm({ value: f, onClose, onSave: submit });
  return (
    <Modal title="Nuevo movimiento" m={m}>
      <form onSubmit={m.guardar} className="space-y-4">
        <Sel label="Cuenta" v={f.cuenta_id} on={(v) => setF({...f, cuenta_id: v})} testid="mov-cuenta">
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.codigo} · {a.nombre}</option>)}
        </Sel>
        <div className="grid grid-cols-2 gap-3">
          <Sel label="Tipo" v={f.tipo} on={(v) => setF({...f, tipo: v})} testid="mov-tipo">
            <option value="ingreso">Ingreso</option>
            <option value="egreso">Egreso</option>
          </Sel>
          <Sel label="Método" v={f.metodo} on={(v) => setF({...f, metodo: v})}>
            <option value="efectivo">Efectivo</option>
            <option value="cheque">Cheque</option>
            <option value="transferencia">Transferencia</option>
            <option value="tarjeta">Tarjeta</option>
            <option value="deposito">Depósito</option>
          </Sel>
        </div>
        <Inp label="Concepto *" v={f.concepto} on={(v) => setF({...f, concepto: v})} required testid="mov-concepto" />
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Monto (L.) *" type="number" step="0.01" v={f.monto} on={(v) => setF({...f, monto: v})} required testid="mov-monto" />
          <Inp label="Referencia" v={f.referencia} on={(v) => setF({...f, referencia: v})} />
        </div>
        <button type="submit" data-testid="mov-submit" disabled={m.saving} className="w-full bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-3">Registrar movimiento</button>
      </form>
    </Modal>
  );
}

function AccountModal({ account, onClose, onSaved }) {
  const [f, setF] = useState(account || {
    codigo: "", nombre: "", tipo: "banco", banco: "", numero_cuenta: "",
    moneda: "HNL", saldo_inicial: 0, cuenta_contable: "", notas: "",
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (account) await api.put(`/treasury/accounts/${account.id}`, { ...f, saldo_inicial: Number(f.saldo_inicial) });
      else await api.post("/treasury/accounts", { ...f, saldo_inicial: Number(f.saldo_inicial) });
      toast.success(account ? "Cuenta actualizada" : "Cuenta creada"); onSaved();
    } catch (er) { toast.error(apiErr(er)); }
  };
  const m = useModalForm({ value: f, onClose, onSave: submit });
  return (
    <Modal title={account ? "Editar cuenta" : "Nueva cuenta"} m={m}>
      <form onSubmit={m.guardar} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Código *" v={f.codigo} on={(v) => setF({...f, codigo: v})} required />
          <Sel label="Tipo" v={f.tipo} on={(v) => setF({...f, tipo: v})}>
            <option value="caja">Caja</option><option value="banco">Banco</option><option value="tarjeta">Tarjeta</option>
          </Sel>
        </div>
        <Inp label="Nombre *" v={f.nombre} on={(v) => setF({...f, nombre: v})} required />
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Banco" v={f.banco} on={(v) => setF({...f, banco: v})} />
          <Inp label="N° Cuenta" v={f.numero_cuenta} on={(v) => setF({...f, numero_cuenta: v})} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Sel label="Moneda" v={f.moneda} on={(v) => setF({...f, moneda: v})}>
            <option value="HNL">HNL</option><option value="USD">USD</option><option value="EUR">EUR</option>
          </Sel>
          <Inp label="Saldo inicial" type="number" step="0.01" v={f.saldo_inicial} on={(v) => setF({...f, saldo_inicial: v})} />
          <Inp label="Cta contable (vacío = crear subcuenta automática)" v={f.cuenta_contable} on={(v) => setF({...f, cuenta_contable: v})} />
        </div>
        <button type="submit" disabled={m.saving} className="w-full bg-agro-green text-black font-semibold rounded-md py-3 hover:bg-agro-green-hover disabled:opacity-40">Guardar</button>
      </form>
    </Modal>
  );
}

function TransferModal({ accounts, onClose, onSaved }) {
  const [f, setF] = useState({ origen_id: accounts[0]?.id || "", destino_id: accounts[1]?.id || "", monto: 0, concepto: "Transferencia entre cuentas", referencia: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/treasury/transfer", { ...f, monto: Number(f.monto) }); toast.success("Transferencia registrada"); onSaved(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const m = useModalForm({ value: f, onClose, onSave: submit });
  return (
    <Modal title="Transferencia entre cuentas" m={m}>
      <form onSubmit={m.guardar} className="space-y-4">
        <Sel label="Cuenta origen" v={f.origen_id} on={(v) => setF({...f, origen_id: v})} testid="transfer-origen">
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.codigo} · {a.nombre} (saldo {fmtL(a.saldo_actual)})</option>)}
        </Sel>
        <Sel label="Cuenta destino" v={f.destino_id} on={(v) => setF({...f, destino_id: v})} testid="transfer-destino">
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.codigo} · {a.nombre}</option>)}
        </Sel>
        <Inp label="Monto (L.) *" type="number" step="0.01" v={f.monto} on={(v) => setF({...f, monto: v})} required testid="transfer-monto" />
        <Inp label="Concepto" v={f.concepto} on={(v) => setF({...f, concepto: v})} />
        <Inp label="Referencia" v={f.referencia} on={(v) => setF({...f, referencia: v})} />
        <button type="submit" data-testid="transfer-submit" disabled={m.saving} className="w-full bg-agro-yellow hover:bg-agro-yellow-hover text-black font-semibold rounded-md py-3">Transferir fondos</button>
      </form>
    </Modal>
  );
}

function Modal({ title, m, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-lg">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">{title}</h3>
          <BotonCerrar onClick={m.cerrar} />
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
function Inp({ label, v, on, type = "text", step, required, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{label}</label>
      <input data-testid={testid} type={type} step={step} value={v ?? ""} required={required} onChange={(e) => on(e.target.value)}
        className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
    </div>
  );
}
function Sel({ label, v, on, children, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{label}</label>
      <select data-testid={testid} value={v} onChange={(e) => on(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">{children}</select>
    </div>
  );
}
